/**
 * Product types, mirroring `app/api/v1/products/schemas.py` field for field.
 *
 * Read that file before changing anything here. Three of its behaviours are not obvious
 * from the shapes alone and every consumer in this feature depends on them:
 *
 * 1. **`option_groups` is replace-on-write.** Sending it deletes every existing group and
 *    option and recreates them (`service.py:161-190`), so **option and group ids are not
 *    stable across a save**. Nothing may cache them past a mutation.
 * 2. **Scalar fields use `__fields_set__`** (`service.py:142`), so an explicitly-sent
 *    `null` clears a value while an omitted key leaves it untouched. `ProductUpdateInput`
 *    is therefore built from what actually changed, not from the whole form.
 * 3. **`is_featured: false` clears `featured_banner_url` server-side** (`service.py:158`).
 *    The client does not need to send that, and should not fight it.
 */

/** How an option group is chosen. `variant` groups must be `single` — see `optionGroup.ts`. */
export const SELECTION_TYPES = ['single', 'multi', 'stepper'] as const
export type SelectionType = (typeof SELECTION_TYPES)[number]

/**
 * How a group is presented in the customer app.
 *
 * `variant` is the size-style row (one choice, drives the headline price); `modifier` is
 * an add-on. The distinction is the customer app's, not this console's, but it is stored
 * here and constrains `selection_type`.
 */
export const DISPLAY_AS = ['variant', 'modifier'] as const
export type DisplayAs = (typeof DISPLAY_AS)[number]

/** v1's wording (`app.js:3093-3095`), kept so the vocabulary staff already know survives. */
export const SELECTION_TYPE_LABELS: Record<SelectionType, string> = {
  single: 'Single choice',
  multi: 'Multi choice',
  stepper: 'Stepper (qty)',
}

/** v1's wording (`app.js:3087-3088`). */
export const DISPLAY_AS_LABELS: Record<DisplayAs, string> = {
  variant: 'Variant',
  modifier: 'Modifier',
}

/** `OptionResponse`. */
export type ProductOption = {
  id: number
  name: string
  /** Added to the base price when chosen. May be negative. */
  price_modifier: number
  is_default: boolean
  /** Choosing this option consumes this ingredient instead of the default. */
  ingredient_id: number | null
}

/** `OptionGroupResponse`. */
export type ProductOptionGroup = {
  id: number
  name: string
  is_required: boolean
  selection_type: SelectionType
  display_as: DisplayAs
  /** Stepper only: minimum quantity. */
  min_value: number | null
  /** Stepper only: maximum quantity. */
  max_value: number | null
  /** Stepper only: quantity included in the base price. */
  default_value: number | null
  options: ProductOption[]
}

/** `CategoryItem` — a category as returned inside a product. */
export type ProductCategoryRef = {
  id: number
  name: string
}

/**
 * `IngredientMappingItem`.
 *
 * Note the asymmetry: `product_to_response` builds these **without** `option_group_id`
 * (`schemas.py:151`), so it is always null on read even when a row has one. Writing it
 * back is also futile, because saving replaces the option groups that ids would point at.
 * The console therefore treats mappings as ingredient + quantity only.
 */
export type IngredientMapping = {
  ingredient_id: number
  quantity: number
  option_group_id: number | null
}

/** `IncludedIngredientResponse` — the "what's included" display list. */
export type IncludedIngredient = {
  ingredient_id: number
  name: string
  quantity: number
  unit: string | null
  option_group_id: number | null
}

/** `ProductResponse`. Money arrives as `float`, so every amount here is a `number`. */
export type Product = {
  id: number
  name: string
  description: string | null
  base_price: number
  /**
   * Loyalty points to redeem one unit of the base product. `null` means customers cannot
   * pay for this with points. Setting a value is what opts the product in.
   */
  points_price: number | null
  /** 3:2 menu-card image. Relative path under `/uploads`. */
  image_url: string | null
  /** 16:9 carousel banner, only meaningful while `is_featured`. */
  featured_banner_url: string | null
  is_featured: boolean
  is_available: boolean
  /**
   * Primary category *name* — the first of `categories`, falling back to the legacy
   * column (`schemas.py:178`). Kept for backward compatibility; prefer `categories`.
   */
  category: string
  /** Every category this product belongs to, via the join table. */
  categories: ProductCategoryRef[]
  /** Only populated when the list was fetched with `sort=most_popular`. */
  order_count: number | null
  option_groups: ProductOptionGroup[]
  ingredient_mappings: IngredientMapping[]
  included_ingredients: IncludedIngredient[]
  created_at: string | null
  updated_at: string | null
}

export type PaginatedProducts = {
  items: Product[]
  total: number
}

/** `OptionCreate` — what the editor sends for one choice. */
export type OptionInput = {
  name: string
  price_modifier: number
  is_default: boolean
  ingredient_id?: number | undefined
}

/** `OptionGroupCreate` — what the editor sends for one group. */
export type OptionGroupInput = {
  name: string
  is_required: boolean
  selection_type: SelectionType
  display_as: DisplayAs
  min_value?: number | undefined
  max_value?: number | undefined
  default_value?: number | undefined
  options: OptionInput[]
}

/**
 * `ProductCreate`.
 *
 * `category_ids` is preferred over the legacy `category` name; the backend resolves the
 * name to an id only as a fallback (`service.py:150-155`).
 */
export type ProductCreateInput = {
  name: string
  description?: string | null | undefined
  base_price: number
  /** Omit or null = not redeemable with points. */
  points_price?: number | null | undefined
  image_url?: string | null | undefined
  featured_banner_url?: string | null | undefined
  is_featured: boolean
  is_available: boolean
  category_ids?: number[] | undefined
  option_groups: OptionGroupInput[]
  /**
   * **Never sent by this console** — see divergence D20.
   *
   * v1 sent `[]` on every save (`app.js:3577`), and because the backend full-replaces
   * whenever the key is present (`service.py:225`), that silently deleted every mapping
   * the product had. Omitting the key entirely leaves them untouched
   * (`service.py:196`, `if data.ingredient_mappings is not None`).
   *
   * Kept in the type because the endpoint accepts it and a future ingredients screen will
   * need it. If you ever set it, you are choosing to replace the whole list.
   */
  ingredient_mappings?: IngredientMapping[] | undefined
}

/**
 * `ProductUpdate` — every field optional, and *omission is meaningful*.
 *
 * Built from a diff of the form against the loaded product rather than from the whole
 * form, because the backend distinguishes "absent" from "null" (`service.py:142`).
 */
export type ProductUpdateInput = Partial<ProductCreateInput>

/**
 * What `DELETE /products/{id}` returns.
 *
 * A product referenced by any order item is **archived**, not deleted — it is marked
 * unavailable and un-featured so order history keeps resolving (`service.py:405-411`).
 * The two outcomes read very differently to whoever pressed the button, so the response
 * is surfaced rather than swallowed.
 */
export type ProductDeleteResult = {
  action: 'archived' | 'deleted'
  message: string
}

/** The list filters v1 offered, which are mutually exclusive (`app.js:2967`). */
export const PRODUCT_FILTERS = ['all', 'featured', 'most_popular'] as const
export type ProductFilter = (typeof PRODUCT_FILTERS)[number]

export const PRODUCT_FILTER_LABELS: Record<ProductFilter, string> = {
  all: 'All',
  featured: 'Featured',
  most_popular: 'Most popular',
}

/** v1's page size for this screen (`app.js:235`). */
export const PRODUCTS_PAGE_SIZE = 24
